import type {
  ConsumerContractRegistryManifest,
  ConsumerContractSubscription,
  FlatSubscriptionRow,
  NewValuePropagationKind,
  ValueHandlingKind,
  ValueHandlingProfile,
  ValuesSourceKind,
} from './consumerContractRegistry.types';
import { PUBLIC_PDF_MAPPER, SHARED_SUMMARY_BUILDER } from './consumerContractRegistry.relationships';

/**
 * Fields whose getDesignerSummaryRows output branches on specific enum/code values.
 * glass_type, variant, beading_type, handle_color, handle_style, window_type_name and material_name
 * are printed as stored text, so they are not listed here.
 */
export const ENUM_MAPPED_FIELD_KEYS = new Set([
  'trickle_vents',
  'open_direction',
  'hinge_placement',
  'master_leaf',
  'stack_direction',
  'traffic_door',
  'sliding_panels',
  'threshold',
  'glass_specification',
  'cill_option',
  'frame_color_external',
  'frame_color_internal',
  'frame_color_combined',
  'window_hinge_type',
  'astragal_bar_side',
  'profile_definition',
]);

export const TRICKLE_VENTS_FIELD_KEY = 'trickle_vents';

const EXTENDER_EXAMPLE = 'extender';

function profile(
  partial: ValueHandlingProfile,
): ValueHandlingProfile {
  return partial;
}

/** Per subscription overrides from code evidence (artifactId.fieldKey or fieldKey). */
const SUBSCRIPTION_OVERRIDES: Record<string, ValueHandlingProfile> = {
  [`public_customer_pdf.${TRICKLE_VENTS_FIELD_KEY}`]: profile({
    valueHandling: 'pass_through_with_constraints',
    valuesSource: 'sanitisation_whitelist',
    newValuePropagation: 'verification_only',
    valueHandlingRationale:
      'trickle_vents_position must be on the sanitizeVisualMeta allow-list (publicVisualMeta.ts); the wording then comes from getDesignerSummaryRows. Verify the allow-list when adding a position.',
  }),
  'supplier_basket_order.glass_type': profile({
    valueHandling: 'value_aware',
    valuesSource: 'supplier_external_mapping',
    newValuePropagation: 'supplier_contract_update_required',
    valueHandlingRationale:
      'Basket maps glass_type_code "obscured" → Obscured, otherwise Clear (visualDesignerLinesToWindowBasketItems).',
  }),
  'supplier_basket_order.handle_style': profile({
    valueHandling: 'value_aware',
    valuesSource: 'supplier_external_mapping',
    newValuePropagation: 'supplier_contract_update_required',
    valueHandlingRationale:
      'Basket maps handle_style codes (e.g. monkey-tail → Monkey Tail) when no label is stored (visualDesignerLinesToWindowBasketItems).',
  }),
};

/** Code paths affecting trickle location not represented as a dedicated registry subscription. */
export const TRICKLE_VENT_EXTRA_CONSUMERS = [
  {
    id: 'vd-modal-merge-wg-preset',
    name: 'VD modal → WG vent preset',
    valueHandling: 'value_aware' as ValueHandlingKind,
    valuesSource: 'local_hard_coded_list' as ValuesSourceKind,
    newValuePropagation: 'mapping_update_required' as NewValuePropagationKind,
    headExtenderImpact:
      'mergeModalStateIntoPayload maps extender→ventPos extender (jamb-like render); selecting extender sets ext_top_mm ≥ 20 without clearing on position change.',
    codeReference: 'QuoteVisualDesignerCard.tsx#mergeModalStateIntoPayload',
  },
  {
    id: 'staff-pdf-legacy-flat-trickle',
    name: 'Staff PDF legacy flat trickleVents',
    valueHandling: 'value_aware' as ValueHandlingKind,
    valuesSource: 'local_hard_coded_list' as ValuesSourceKind,
    newValuePropagation: 'mapping_update_required' as NewValuePropagationKind,
    headExtenderImpact:
      'DocumentDetailPage uses formatTrickleVentsPositionLabel for extender label in staff PDF flat items.',
    codeReference: 'DocumentDetailPage.tsx#renderTemplate',
  },
  {
    id: 'supplier-basket-trickle-parts',
    name: 'Supplier basket trickle yes/no and position parts',
    valueHandling: 'value_aware' as ValueHandlingKind,
    valuesSource: 'supplier_external_mapping' as ValuesSourceKind,
    newValuePropagation: 'supplier_contract_update_required' as NewValuePropagationKind,
    headExtenderImpact:
      'The supplier PDF TRICKLE VENTS row uses the shared summary; the basket still carries the raw position code for {{trickleVentsPosition}}.',
    codeReference: 'SendVisualDesignerToSupplierModal.tsx#visualDesignerLinesToWindowBasketItems',
  },
  {
    id: 'graph-service-trickle',
    name: 'Supplier order graphService trickle',
    valueHandling: 'value_aware' as ValueHandlingKind,
    valuesSource: 'supplier_external_mapping' as ValuesSourceKind,
    newValuePropagation: 'supplier_contract_update_required' as NewValuePropagationKind,
    headExtenderImpact:
      'graphService uses formatTrickleVentsPositionLabel including Extender label.',
    codeReference: 'src/utils/graphService.ts',
  },
] as const;

export function isImageSubscription(row: Pick<FlatSubscriptionRow, 'fieldKey' | 'accessMode' | 'operation'>): boolean {
  if (row.accessMode === 'cached_derivative') return true;
  if (/^preview_path_/.test(row.fieldKey)) return true;
  if (row.fieldKey === 'modal_stored_png_fallback') return true;
  return false;
}

function overrideKey(row: FlatSubscriptionRow): string | null {
  const specific = `${row.artifactId}.${row.fieldKey}`;
  if (SUBSCRIPTION_OVERRIDES[specific]) return specific;
  if (SUBSCRIPTION_OVERRIDES[row.fieldKey]) return row.fieldKey;
  return null;
}

export function resolveValueHandling(row: FlatSubscriptionRow): ValueHandlingProfile {
  if (isImageSubscription(row)) {
    return profile({
      valueHandling: 'unknown',
      valuesSource: 'unknown',
      newValuePropagation: 'unknown',
      valueHandlingRationale: 'Stored PNG URL path — value-handling governance does not apply.',
      excludedFromValueHandling: true,
    });
  }

  const overrideKeyHit = overrideKey(row);
  if (overrideKeyHit) {
    return { ...SUBSCRIPTION_OVERRIDES[overrideKeyHit], excludedFromValueHandling: false };
  }

  if (row.usage === 'omitted') {
    return profile({
      valueHandling: 'pass_through_with_constraints',
      valuesSource: 'sanitisation_whitelist',
      newValuePropagation: 'validation_sanitisation_update_required',
      valueHandlingRationale: 'Field stripped by sanitizeVisualMeta whitelist on public edge payloads.',
    });
  }

  if (row.fieldKey === 'quantity' || row.fieldKey === 'reference') {
    return profile({
      valueHandling: 'value_agnostic',
      valuesSource: 'unrestricted',
      newValuePropagation: 'automatic',
      valueHandlingRationale: 'Opaque string/number formatting without enum branching.',
    });
  }

  if (ENUM_MAPPED_FIELD_KEYS.has(row.fieldKey)) {
    if (row.audience === 'supplier' && row.operation === 'calculate_derive') {
      return profile({
        valueHandling: 'value_aware',
        valuesSource: 'supplier_external_mapping',
        newValuePropagation: 'supplier_contract_update_required',
        valueHandlingRationale: 'Supplier basket/order projection maps codes to external supplier labels.',
      });
    }
    if (row.viaBuilder === PUBLIC_PDF_MAPPER) {
      return profile({
        valueHandling: 'value_aware',
        valuesSource: 'local_hard_coded_list',
        newValuePropagation: 'mapping_update_required',
        valueHandlingRationale: 'Public PDF parallel mapper uses enum/format branches (public-doc/index.ts).',
      });
    }
    if (row.viaBuilder === SHARED_SUMMARY_BUILDER || row.codeReference.includes('getDesignerSummaryRows')) {
      return profile({
        valueHandling: 'value_aware',
        valuesSource: 'local_hard_coded_list',
        newValuePropagation: 'mapping_update_required',
        valueHandlingRationale: 'getDesignerSummaryRows uses fixed ternary/switch label mapping for this field.',
      });
    }
    if (row.operation === 'read_and_format') {
      return profile({
        valueHandling: 'value_aware',
        valuesSource: 'local_hard_coded_list',
        newValuePropagation: 'mapping_update_required',
        valueHandlingRationale: 'Direct read_and_format path with enum label mapping.',
      });
    }
  }

  if (row.relationshipKind === 'canonical_reference' || row.fieldKey === 'templatePayload') {
    return profile({
      valueHandling: 'value_agnostic',
      valuesSource: 'canonical_contract',
      newValuePropagation: 'automatic',
      valueHandlingRationale: 'Reads canonical templatePayload shape without enum label mapping.',
    });
  }

  if (row.operation === 'calculate_derive' || row.relationshipKind === 'compatibility_overlay') {
    return profile({
      valueHandling: 'value_aware',
      valuesSource: 'canonical_contract',
      newValuePropagation: 'verification_only',
      valueHandlingRationale: 'Merge/derive path may interpret specs values when overlaying payload.',
    });
  }

  if (row.operation === 'read_and_format' || row.operation === 'read_only') {
    return profile({
      valueHandling: 'value_agnostic',
      valuesSource: 'canonical_contract',
      newValuePropagation: 'automatic',
      valueHandlingRationale: 'Read path without verified enum branching on this field.',
    });
  }

  return profile({
    valueHandling: 'unknown',
    valuesSource: 'unknown',
    newValuePropagation: 'unknown',
    valueHandlingRationale: 'Insufficient code evidence for value-handling classification.',
  });
}

export function attachValueHandling(
  row: Omit<
    FlatSubscriptionRow,
    'valueHandling' | 'valuesSource' | 'newValuePropagation' | 'valueHandlingRationale' | 'excludedFromValueHandling'
  >,
): FlatSubscriptionRow {
  const vh = resolveValueHandling(row as FlatSubscriptionRow);
  return {
    ...row,
    valueHandling: vh.valueHandling,
    valuesSource: vh.valuesSource,
    newValuePropagation: vh.newValuePropagation,
    valueHandlingRationale: vh.valueHandlingRationale,
    excludedFromValueHandling: !!vh.excludedFromValueHandling,
  };
}

function flattenPlain(manifest: ConsumerContractRegistryManifest): FlatSubscriptionRow[] {
  const rows: FlatSubscriptionRow[] = [];
  for (const artifact of manifest.artifacts) {
    for (const subscription of artifact.subscriptions) {
      rows.push(
        attachValueHandling({
          ...(subscription as ConsumerContractSubscription),
          artifactId: artifact.id,
          artifactName: artifact.name,
          audience: artifact.audience,
          renderSource: artifact.renderSource,
        }),
      );
    }
  }
  return rows;
}

export function flattenWithValueHandling(
  manifest: ConsumerContractRegistryManifest,
): FlatSubscriptionRow[] {
  return flattenPlain(manifest);
}

export function listNonImageValueHandlingRows(
  manifest: ConsumerContractRegistryManifest,
): FlatSubscriptionRow[] {
  return flattenWithValueHandling(manifest).filter((r) => !r.excludedFromValueHandling);
}

export interface ValueHandlingCounts {
  valueHandling: Record<ValueHandlingKind, number>;
  valuesSource: Record<ValuesSourceKind, number>;
  newValuePropagation: Record<NewValuePropagationKind, number>;
  totalNonImage: number;
  totalExcludedImage: number;
}

function emptyCounts<T extends string>(keys: readonly T[]): Record<T, number> {
  return Object.fromEntries(keys.map((k) => [k, 0])) as Record<T, number>;
}

export function countValueHandlingClassifications(
  manifest: ConsumerContractRegistryManifest,
): ValueHandlingCounts {
  const flat = flattenWithValueHandling(manifest);
  const valueHandling = emptyCounts([
    'value_agnostic',
    'value_aware',
    'pass_through_with_constraints',
    'unknown',
  ] as const);
  const valuesSource = emptyCounts([
    'canonical_contract',
    'shared_configuration',
    'local_hard_coded_list',
    'sanitisation_whitelist',
    'supplier_external_mapping',
    'unrestricted',
    'unknown',
  ] as const);
  const newValuePropagation = emptyCounts([
    'automatic',
    'verification_only',
    'mapping_update_required',
    'validation_sanitisation_update_required',
    'supplier_contract_update_required',
    'unknown',
  ] as const);

  let totalNonImage = 0;
  let totalExcludedImage = 0;

  for (const row of flat) {
    if (row.excludedFromValueHandling) {
      totalExcludedImage += 1;
      continue;
    }
    totalNonImage += 1;
    valueHandling[row.valueHandling] += 1;
    valuesSource[row.valuesSource] += 1;
    newValuePropagation[row.newValuePropagation] += 1;
  }

  return { valueHandling, valuesSource, newValuePropagation, totalNonImage, totalExcludedImage };
}

export interface TrickleVentImpactRow {
  consumer: string;
  artifactId?: string;
  fieldKey: string;
  valueHandling: ValueHandlingKind;
  newValuePropagation: NewValuePropagationKind;
  headExtenderImpact: string;
  inRegistry: boolean;
}

export function buildTrickleVentImpactMatrix(
  manifest: ConsumerContractRegistryManifest,
): TrickleVentImpactRow[] {
  const rows: TrickleVentImpactRow[] = [];
  const flat = flattenWithValueHandling(manifest).filter(
    (r) => r.fieldKey === TRICKLE_VENTS_FIELD_KEY && !r.excludedFromValueHandling,
  );

  for (const row of flat) {
    rows.push({
      consumer: row.artifactName,
      artifactId: row.artifactId,
      fieldKey: row.fieldKey,
      valueHandling: row.valueHandling,
      newValuePropagation: row.newValuePropagation,
      headExtenderImpact: describeHeadExtenderImpact(row.newValuePropagation, row.artifactId),
      inRegistry: true,
    });
  }

  for (const extra of TRICKLE_VENT_EXTRA_CONSUMERS) {
    rows.push({
      consumer: extra.name,
      fieldKey: TRICKLE_VENTS_FIELD_KEY,
      valueHandling: extra.valueHandling,
      newValuePropagation: extra.newValuePropagation,
      headExtenderImpact: extra.headExtenderImpact,
      inRegistry: false,
    });
  }

  return rows;
}

function describeHeadExtenderImpact(propagation: NewValuePropagationKind, artifactId?: string): string {
  switch (propagation) {
    case 'automatic':
      return `${EXTENDER_EXAMPLE} would display without code changes.`;
    case 'verification_only':
      return `${EXTENDER_EXAMPLE} passes sanitize whitelist; verify customer-facing label (${artifactId ?? 'consumer'}).`;
    case 'mapping_update_required':
      return `${EXTENDER_EXAMPLE} needs label/preset mapping; otherwise falls back to generic "Yes".`;
    case 'validation_sanitisation_update_required':
      return `${EXTENDER_EXAMPLE} requires sanitizeVisualMeta whitelist entry.`;
    case 'supplier_contract_update_required':
      return `${EXTENDER_EXAMPLE} needs supplier/graphService label mapping.`;
    default:
      return `Impact of ${EXTENDER_EXAMPLE} unverified for this consumer.`;
  }
}

export function countFieldNewValueImpact(subscribers: FlatSubscriptionRow[]): Record<NewValuePropagationKind, number> {
  const counts = emptyCounts([
    'automatic',
    'verification_only',
    'mapping_update_required',
    'validation_sanitisation_update_required',
    'supplier_contract_update_required',
    'unknown',
  ] as const);
  for (const row of subscribers) {
    if (row.excludedFromValueHandling) continue;
    counts[row.newValuePropagation] += 1;
  }
  return counts;
}
